import type {
    ContentBlock,
    IChatAttachment,
    IChatAttachmentBrief,
    IChatMessage,
} from "@/api/chat/chat.types";
import { MessageRole } from "@/api/chat/chat.types";
import { isDisplayContentBlock } from "@/api/chat/content-blocks";
import type { IReportVersionSummary } from "@/api/reports/reports.types";
import type { ThreadMessageLike } from "@assistant-ui/react";
import { registerAttachmentFileType } from "@/components/shared/attachment-file-type-registry";
import type { ChainStep } from "../components/ChainOfThought";
import { mapContentBlock, reportDataPart } from "./content-parts";

/**
 * The wire marker for a turn the user deliberately ended, written only where the
 * backend records `SUPERSEDED_BY_USER`. Every other failure keeps its own type.
 *
 * It shares a spelling with assistant-ui's `cancelled` status reason and is a
 * different vocabulary: one is the backend's `error_type` column, the other is
 * the runtime's. The mapping below is a translation, not a pass-through.
 */
const CANCELLED_ERROR_TYPE = "cancelled";

/**
 * Classify an ended turn that carries an `error_type`.
 *
 * `MessagePrimitive.Error` renders for `incomplete` + `error` and nothing else,
 * so the reason is what decides whether a stopped answer comes back looking like
 * a failure. A turn the user stopped did not fail, and the owner's own tab
 * already shows it stopped -- reloading it as an error would contradict what the
 * person watched happen.
 */
function endedStatus(
    errorType: string,
    errorMessage: string | null | undefined
): ThreadMessageLike["status"] {
    if (errorType === CANCELLED_ERROR_TYPE) {
        return { type: "incomplete" as const, reason: "cancelled" as const };
    }
    return {
        type: "incomplete" as const,
        reason: "error" as const,
        error: errorMessage || "An error occurred. Please try again.",
    };
}

/**
 * Converts backend IChatMessage[] to assistant-ui ThreadMessageLike[].
 *
 * Each report row whose user message is in the history adds one `report` part
 * to the assistant message directly after that user message, whether or not
 * the turn kept its tool block.
 */
export function convertMessages(
    messages: IChatMessage[],
    conversationAttachments?: IChatAttachment[],
    reportRows: readonly Pick<IReportVersionSummary, "chat_message_id">[] = []
): ThreadMessageLike[] {
    if (messages.length === 0) return [];

    const reportMessageIds = new Set(
        reportRows.map((row) => row.chat_message_id)
    );

    // First pass: find the most recent assistant message holding a
    // non-dismissed / non-refined context_enrichment block. That message's
    // card is the single actionable one; all others collapse.
    const latestActionableMessageId =
        findLatestActionableEnrichmentMessageId(messages);

    const result: ThreadMessageLike[] = [];

    for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];

        if (msg.role === MessageRole.User) {
            result.push({
                id: String(msg.id),
                role: "user",
                content: msg.content || "",
                createdAt: new Date(msg.created_at),
                attachments: msg.attachments?.map((a) => {
                    // Type-prefixed id (matches toAuiAttachment on the composer
                    // path): a bare numeric id collides when a message carries a
                    // document and a non-document sharing the same numeric id
                    // (e.g. document #5 and scope #5) -- the scope's null
                    // file_type registration would then delete the document's
                    // entry. "type:id" keeps them distinct.
                    const id = `${a.attachment_type}:${a.attachment_id}`;
                    // Carry the backend-resolved file_type onto the
                    // side-channel keyed by the reload id, so the reloaded pill
                    // renders a spreadsheet (green) vs a PDF (red).
                    // CompleteAttachment has no slot for it.
                    registerAttachmentFileType(id, a.file_type);
                    return {
                        id,
                        type: a.attachment_type as string,
                        name: a.attachment_name,
                        status: { type: "complete" as const },
                        content: [],
                    };
                }),
                metadata: {
                    custom: {
                        sender: msg.sender,
                    },
                },
            });
        } else {
            // Assistant message
            const content: unknown[] = [];

            // Group consecutive CoT blocks (thinking, tool_use, processing)
            // with text blocks breaking the grouping to match the render
            // interleave pattern.
            if (msg.content_blocks && msg.content_blocks.length > 0) {
                let cotSteps: ChainStep[] | null = null;

                for (const [index, block] of msg.content_blocks.entries()) {
                    if (
                        !isDisplayContentBlock(block, msg.content_blocks, index)
                    ) {
                        continue;
                    }
                    if (
                        block.type === "thinking" ||
                        block.type === "tool_use" ||
                        block.type === "processing"
                    ) {
                        cotSteps ??= [];
                        cotSteps.push(blockToChainStep(block));
                    } else {
                        if (cotSteps) {
                            content.push({
                                type: "data",
                                name: "chain-of-thought",
                                data: { steps: cotSteps },
                            });
                            cotSteps = null;
                        }
                        content.push(
                            mapContentBlock(block, {
                                isLatestActionable:
                                    block.type === "context_enrichment" &&
                                    msg.id === latestActionableMessageId,
                                conversationId:
                                    block.type === "context_enrichment"
                                        ? msg.conversation_id
                                        : undefined,
                                messageId:
                                    block.type === "context_enrichment"
                                        ? msg.id
                                        : undefined,
                            })
                        );
                    }
                }

                if (cotSteps) {
                    content.push({
                        type: "data",
                        name: "chain-of-thought",
                        data: { steps: cotSteps },
                    });
                }
            } else {
                content.push({ type: "text", text: msg.content || "" });
            }

            const answered = messages[i - 1];
            if (
                answered?.role === MessageRole.User &&
                reportMessageIds.has(answered.id)
            ) {
                content.push(
                    reportDataPart({
                        conversationId: msg.conversation_id,
                        chatMessageId: answered.id,
                    })
                );
            }

            // Append citation source parts
            if (msg.citations && msg.citations.length > 0) {
                for (const c of msg.citations) {
                    if (!c.document_url) continue;
                    content.push({
                        type: "source",
                        sourceType: "url",
                        id: String(c.document_id),
                        url: c.document_url,
                        title: c.document_name,
                    });
                }
            }

            // Filter conversationAttachments to those matching the preceding user message
            const precedingUser = findPrecedingUser(messages, i);
            const filteredAttachments = filterConversationAttachments(
                conversationAttachments,
                precedingUser?.attachments
            );

            // Append references data part
            content.push({
                type: "data",
                name: "references",
                data: {
                    citations: msg.citations || [],
                    autoAttachments: msg.attachments || [],
                    conversationAttachments: filteredAttachments,
                },
            });

            // Carry persisted inline-citation resolutions so reload hydrates
            // the cite:// chips exactly like the live citation_resolved stream
            // event. `metadata.custom` is the assistant-ui channel every
            // consumer reads via useAuiState (thread.tsx, the table toolbar,
            // and the markdown/xlsx exporters), matching the adapter's live
            // shape.
            const citationResolutions = msg.citation_resolutions ?? [];
            // A recovering turn is still being finished server-side, so it
            // renders as running (spinner treatment) rather than as an error.
            // The accumulated partial stays in `content` either way -- hiding
            // it would be strictly worse than the interrupted rendering it
            // replaces.
            const isRecovering = msg.recovery_display === "recovering";
            const converted: ThreadMessageLike = {
                id: String(msg.id),
                role: "assistant",
                // ThreadMessageLike accepts AppendContentPart[] which is
                // broader than our literal objects; the cast is unavoidable
                // without re-exporting assistant-ui's internal part types.
                content: content as ThreadMessageLike["content"],
                status: isRecovering
                    ? { type: "running" as const }
                    : msg.error_type
                      ? endedStatus(msg.error_type, msg.error_message)
                      : { type: "complete", reason: "stop" },
                createdAt: new Date(msg.created_at),
                metadata: {
                    custom: {
                        citation_resolutions: citationResolutions,
                        recovery_display: msg.recovery_display,
                        // Carried on the message so the recovering affordance
                        // never has to reach into thread-list scope for it.
                        conversation_id: msg.conversation_id,
                        // Lets the parked card render on import rather than
                        // after the first poll; any status supersedes it.
                        waiting_for_documents: msg.waiting_for_documents,
                    },
                },
            };
            result.push(converted);
        }
    }

    return result;
}

/**
 * Backward scan for the most recent assistant message whose content_blocks
 * include a non-dismissed / non-refined context_enrichment block. Returns
 * `undefined` if no actionable card exists. Used to annotate the emitted
 * data part so only one card in the history renders its chips at a time.
 */
function findLatestActionableEnrichmentMessageId(
    messages: IChatMessage[]
): number | undefined {
    for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.role !== MessageRole.Assistant || !msg.content_blocks) continue;
        const hasActionable = msg.content_blocks.some(
            (b) => b.type === "context_enrichment" && !b.dismissed && !b.refined
        );
        if (hasActionable) return msg.id;
    }
    return undefined;
}

function findPrecedingUser(
    messages: IChatMessage[],
    assistantIndex: number
): IChatMessage | undefined {
    for (let j = assistantIndex - 1; j >= 0; j--) {
        if (messages[j].role === MessageRole.User) {
            return messages[j];
        }
    }
    return undefined;
}

function filterConversationAttachments(
    conversationAttachments: IChatAttachment[] | undefined,
    userBriefs: IChatAttachmentBrief[] | undefined
): IChatAttachment[] {
    if (!conversationAttachments || !userBriefs || userBriefs.length === 0) {
        return [];
    }
    const briefIdentities = new Set(
        userBriefs.map(
            (brief) => `${brief.attachment_type}:${brief.attachment_id}`
        )
    );
    return conversationAttachments.filter((attachment) =>
        briefIdentities.has(
            `${attachment.attachment_type}:${attachment.attachment_id}`
        )
    );
}

function blockToChainStep(block: ContentBlock): ChainStep {
    switch (block.type) {
        case "thinking":
            return {
                type: "thinking",
                text: block.thinking,
                status: "complete",
                durationMs: block.durationMs,
            };
        case "tool_use":
            return {
                type: "tool",
                toolName: block.name,
                toolCallId: block.id,
                status: "complete",
                durationMs: block.durationMs,
                args: block.input,
                loadingText: block.loadingText,
                resultSummary: block.resultSummary,
                result: block.result,
            };
        case "processing":
            return {
                type: "processing",
                name: block.name,
                status: block.status === "error" ? "error" : "complete",
                summaryText: block.summary_text,
                durationMs: block.durationMs,
            };
        default:
            return {
                type: "processing",
                name: "unknown",
                status: "complete",
            };
    }
}
